import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import QuestionCategory from './components/QuestionCategory';

const mockUser = { id: 1, name: 'Sarah Mitchell', email: 'sarah@email.com', role: 'user' };

const questionData = [
  {
    category: 'Pricing & Motivation',
    icon: 'DollarSign',
    color: '#00D4FF',
    questions: [
      "What is the vendor's preferred settlement timeline?",
      'Has the property had any price reductions during this campaign?',
      'Are there any other offers currently on the table?',
      "What is the vendor's minimum acceptable price?",
      'Why is the vendor selling at this time?',
      'Would the vendor consider a longer settlement in exchange for a higher price?',
    ],
  },
  {
    category: 'Property Condition',
    icon: 'Home',
    color: '#FFAA00',
    questions: [
      'Can you provide a copy of the building and pest inspection report?',
      'Are there any known structural issues or defects with the property?',
      'When was the roof last inspected or replaced?',
      'Are there any unapproved structures or extensions on the property?',
      'Has the property experienced any flooding, water damage, or subsidence?',
      'What is the age of the hot water system, HVAC, and major appliances?',
    ],
  },
  {
    category: 'Legal & Title',
    icon: 'FileText',
    color: '#AA88FF',
    questions: [
      'Are there any easements, covenants, or encumbrances on the title?',
      'Are there any pending council orders, notices, or disputes?',
      'Is the property subject to any heritage overlay or planning restrictions?',
      'Can you provide a copy of the Section 32 / Vendor Statement?',
      'Are there any outstanding rates, levies, or body corporate fees?',
      'Has the property been subject to any insurance claims in the past 5 years?',
    ],
  },
  {
    category: 'Neighbourhood & Location',
    icon: 'MapPin',
    color: '#00FF88',
    questions: [
      'Are there any planned developments or rezoning in the immediate area?',
      'What is the noise level like during peak traffic hours?',
      'Are there any known issues with the neighbours?',
      'What is the flood risk or bushfire risk rating for this property?',
      'How close is the property to public transport, schools, and amenities?',
      'Are there any flight paths, train lines, or industrial areas nearby?',
    ],
  },
  {
    category: 'Negotiation & Process',
    icon: 'Handshake',
    color: '#FF8844',
    questions: [
      'What is the preferred method of sale — private treaty or auction?',
      'What conditions would the vendor prefer in the contract?',
      'Is the vendor open to a subject-to-finance clause?',
      'What is the deposit amount and when is it due?',
      'Would the vendor consider including any fixtures or chattels in the sale?',
      'What is the process if there are multiple offers?',
    ],
  },
];

const AgentQuestions = () => {
  const navigate = useNavigate();
  const [copiedAll, setCopiedAll] = useState(false);
  const [search, setSearch] = useState('');

  const allQuestions = questionData?.flatMap(c => c?.questions);

  const handleCopyAll = () => {
    const text = questionData?.map(c => `${c?.category}:\n${c?.questions?.map((q, i) => `${i + 1}. ${q}`)?.join('\n')}`)?.join('\n\n');
    navigator.clipboard?.writeText(text)?.catch(() => {});
    setCopiedAll(true);
    setTimeout(() => setCopiedAll(false), 2000);
  };

  const filteredData = search?.trim()
    ? questionData?.map(c => ({ ...c, questions: c?.questions?.filter(q => q?.toLowerCase()?.includes(search?.toLowerCase())) }))?.filter(c => c?.questions?.length > 0)
    : questionData;

  return (
    <div className="min-h-screen bg-background">
      <Header user={mockUser} onLogout={() => navigate('/authentication')} />
      <main className="pt-16">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-12">
          <div className="mb-8">
            <button onClick={() => navigate('/')} className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-4 text-sm">
              <Icon name="ArrowLeft" size={16} color="currentColor" />
              Back to Home
            </button>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(0,212,255,0.15)', border: '1px solid rgba(0,212,255,0.3)' }}>
                <Icon name="MessageSquare" size={20} color="#00D4FF" />
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground">Agent Questions</h1>
            </div>
            <p className="text-muted-foreground">Pre-written question templates to ask the selling agent. Uncover hidden issues and negotiate effectively.</p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="flex-1 bg-card rounded-xl border border-border p-4 flex items-center gap-3">
              <Icon name="HelpCircle" size={20} color="#00D4FF" />
              <div>
                <p className="text-xl font-bold text-foreground">{allQuestions?.length}</p>
                <p className="text-xs text-muted-foreground">Total Questions</p>
              </div>
            </div>
            <div className="flex-1 bg-card rounded-xl border border-border p-4 flex items-center gap-3">
              <Icon name="Tag" size={20} color="#00FF88" />
              <div>
                <p className="text-xl font-bold text-foreground">{questionData?.length}</p>
                <p className="text-xs text-muted-foreground">Categories</p>
              </div>
            </div>
            <button onClick={handleCopyAll} className="flex items-center gap-2 px-5 py-3 rounded-xl font-semibold text-sm transition-all" style={{ background: 'linear-gradient(135deg, #00D4FF, #0099CC)', color: '#000', boxShadow: '0 0 20px rgba(0,212,255,0.3)' }}>
              <Icon name={copiedAll ? 'Check' : 'Copy'} size={16} color="#000" />
              {copiedAll ? 'Copied!' : 'Copy All Questions'}
            </button>
          </div>

          <div className="relative mb-6">
            <Icon name="Search" size={18} color="#888" className="absolute left-4 top-1/2 -translate-y-1/2" />
            <input type="text" value={search} onChange={e => setSearch(e?.target?.value)} placeholder="Search questions..." className="w-full pl-11 pr-4 py-3 rounded-xl bg-card border border-border text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary transition-colors" />
          </div>

          <div className="space-y-4">
            {filteredData?.map((cat) => (
              <QuestionCategory key={cat?.category} category={cat?.category} questions={cat?.questions} icon={cat?.icon} color={cat?.color} />
            ))}
            {filteredData?.length === 0 && (
              <div className="text-center py-12 text-muted-foreground">
                <Icon name="Search" size={32} color="#888" />
                <p className="mt-2">No questions match your search.</p>
              </div>
            )}
          </div>

          <div className="mt-8 bg-card rounded-xl border border-border p-6">
            <h3 className="font-semibold text-foreground mb-2">Ready to make an offer?</h3>
            <p className="text-sm text-muted-foreground mb-4">Use these questions to gather information, then calculate your optimal offer range.</p>
            <div className="flex flex-wrap gap-3">
              <button onClick={() => navigate('/offer-range-guidance')} className="px-4 py-2 rounded-lg text-sm font-medium" style={{ background: 'rgba(0,255,136,0.1)', color: '#00FF88', border: '1px solid rgba(0,255,136,0.3)' }}>Calculate Offer Range →</button>
              <button onClick={() => navigate('/risk-assessment')} className="px-4 py-2 rounded-lg text-sm font-medium" style={{ background: 'rgba(255,68,68,0.1)', color: '#FF6666', border: '1px solid rgba(255,68,68,0.3)' }}>Check Risks →</button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AgentQuestions;
